// lib
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
// react
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
// mui
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
// api
import { createTarget, useGetOptimizationTarget } from 'src/api/admin';
// component
import FormProvider, { RHFDecimalField } from 'src/components/hook-form';
// hooks
import { useLocales } from 'src/locales';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

export default function CreateTarget({
  dialog,
  onTargetUpdated,
}: {
  dialog: { value: boolean; onFalse: () => void };
  onTargetUpdated: () => void;
}) {
  const { t } = useLocales('financialPage');

  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const NewTargetSchema = Yup.object().shape({
    targetDIO: Yup.number(),
    targetReductionRate: Yup.number().moreThan(
      0,
      `${t('target_reduction_rate')} ${t('must_be_greater_than')}`,
    ),
    startDate: Yup.date().required('is_required'),
    targetDate: Yup.date()
      .required('is_required')
      .when('startDate', (startDate, schema) =>
        startDate
          ? schema.min(startDate, `${t('target_date')} ${t('cannot_be_before')} ${t('start_date')}`)
          : schema,
      ),
  });

  const { data: optimizationTarget, refetch } = useGetOptimizationTarget();

  const defaultValues = useMemo(
    () => ({
      targetDIO: optimizationTarget?.targetDIO || 0,
      targetReductionRate: optimizationTarget?.targetReductionRate || 0,
      targetDate: optimizationTarget?.targetDate ? new Date(optimizationTarget.targetDate) : null,
      startDate: optimizationTarget?.startDate ? new Date(optimizationTarget.startDate) : null,
    }),
    [
      optimizationTarget?.targetDIO,
      optimizationTarget?.targetReductionRate,
      optimizationTarget?.targetDate,
      optimizationTarget?.startDate,
    ],
  );

  const methods = useForm({
    resolver: yupResolver<any, any, any>(NewTargetSchema),
    defaultValues,
  });

  const {
    reset,
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = methods;

  const startDateValue = watch('startDate');

  useEffect(() => {
    // seeding-guard: the dialog stays mounted and Cancel does not reset, so keepDirtyValues
    // would make abandoned edits reappear on reopen. Note this seeds from a singleton
    // query (useGetOptimizationTarget takes no id), so there is no second record to leak
    // into — the exposure is stale drafts, not cross-record writes. Needs a lifecycle
    // reset. See NAT-2311.
    if (optimizationTarget) {
      reset(defaultValues);
    }
  }, [optimizationTarget, reset, defaultValues]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (data) {
        await createTarget(data);
        await refetch();
        enqueueSnackbar(`${t('create_success')}!`);
        onTargetUpdated();
        dialog.onFalse();
      } else {
        enqueueSnackbar(`${t('failed')}!`, { variant: 'error' });
      }
    } catch (error) {
      if (error?.response?.data) {
        enqueueSnackbar(error.response.data.message, { variant: 'error' });
      }
    }
  });

  const renderDetails = (
    <Grid size={12}>
      <Stack spacing={3}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">{t('target_dio')}</Typography>
          <RHFDecimalField name="targetDIO" />
        </Stack>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">{t('target_reduction_rate')}</Typography>
          <RHFDecimalField name="targetReductionRate" />
        </Stack>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">{t('start_date')}</Typography>
          <Controller
            name="startDate"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                value={field.value ? dayjs(field.value) : null}
                format="DD/MM/YYYY"
                onChange={(newValue) => {
                  field.onChange(newValue ? newValue.toDate() : null);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!error,
                    helperText: error?.message,
                  },
                }}
              />
            )}
          />
        </Stack>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">{t('target_date')}</Typography>
          <Controller
            name="targetDate"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                value={field.value ? dayjs(field.value) : null}
                format="DD/MM/YYYY"
                onChange={(newValue) => {
                  field.onChange(newValue ? newValue.toDate() : null);
                }}
                disabled={!startDateValue}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!error,
                    helperText: error?.message,
                  },
                }}
              />
            )}
          />
        </Stack>
      </Stack>
    </Grid>
  );

  const renderActions = (
    <Grid sx={{ display: 'flex', justifyContent: 'flex-end' }} size={12}>
      <Button onClick={() => dialog.onFalse()}>{t('cancel')}</Button>
      <Button type="submit" size="large" loading={isSubmitting} sx={{ ml: 2 }} color="error">
        {t('set')}
      </Button>
    </Grid>
  );

  return (
    <Dialog
      open={dialog.value}
      onClose={dialog.onFalse}
      maxWidth={isExtraSmallScreen ? false : 'md'}
      slotProps={{
        paper: {
          sx: {
            width: isExtraSmallScreen ? '100%' : 'auto',
            padding: 1,
          },
        },
      }}
    >
      <DialogTitle>{`${t('set')} ${t('target')}`}</DialogTitle>
      <DialogContent>
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <Grid container spacing={{ xs: 1, sm: 3 }}>
            {renderDetails}
            {renderActions}
          </Grid>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
